import { debounce } from 'utils/general'
import { getCart } from 'utils/requests'
import { reRenderShopifySections } from 'utils/shopify'

class GiftWithPurchase {
  constructor() {
    this.settings = this.loadSettings()
    if (!this.settings?.enabled) {
      console.debug('[GWP] Gift with purchase is disabled')
      return
    }

    this.isProcessing = false
    this.gwpItemInCart = null
    this.debouncedCheckCart = debounce(this.checkCartAndManageGift.bind(this), 250)

    console.debug('[GWP] Initializing Gift with Purchase', this.settings)

    this.init()
  }

  loadSettings() {
    const settingsElement = document.getElementById('gwp-settings')
    if (!settingsElement) {
      console.warn('[GWP] Settings not found')
      return null
    }

    try {
      return JSON.parse(settingsElement.textContent)
    } catch (error) {
      console.error('[GWP] Failed to parse settings:', error)
      return null
    }
  }

  init() {
    // Listen for cart intercept events from analytics.js
    document.addEventListener('cart:intercept', this.handleCartIntercept.bind(this))

    this.debouncedCheckCart()
  }

  handleCartIntercept(event) {
    console.log('handleCartIntercept', event)

    if (!event.detail.response) {
      console.debug('[GWP] Cart intercept devoid of response:', event.detail)
      return
    }

    if (!event.detail.data) {
      console.debug('[GWP] Cart intercept devoid of data:', event.detail)
      return
    }

    const { action, data, response } = event.detail

    if (!response.ok) {
      console.debug('[GWP] Cart intercept failed:', action, data, response)
      return
    }

    if (action) {
      console.debug('[GWP] Cart intercept detected:', action, data, response)

      if (data) {
        this.debouncedCheckCartWithData(data)
      }
    }
  }

  // Create a debounced version that accepts cart data
  debouncedCheckCartWithData = debounce(cartData => {
    this.checkCartAndManageGift(cartData)
  }, 250)

  async checkCartAndManageGift(cartData = null) {
    if (this.isProcessing) {
      console.debug('[GWP] Already processing, skipping')
      return
    }

    this.isProcessing = true

    try {
      // Use provided cart data or fetch it if not available
      const cart = cartData?.items ? cartData : await getCart()
      const cartTotal = cart.total_price
      const qualifiesForGift = cartTotal >= this.settings.threshold

      console.debug('[GWP] Cart:', cart)
      console.debug(
        '[GWP] Cart total:',
        cartTotal,
        'Threshold:',
        this.settings.threshold,
        'Qualifies:',
        qualifiesForGift
      )

      // Find existing GWP item in cart
      this.gwpItemInCart = cart.items.find(
        item =>
          !item.properties?.['_bundle_id'] &&
          (item.variant_id == this.settings.variantId || item.properties?.[this.settings.lineItemProperty] === 'true')
      )

      if (!this.isUSCustomer()) {
        this.removeGiftFromCart()
        console.debug('[GWP] Not a US customer, skipping')
        return
      }

      if (qualifiesForGift && !this.gwpItemInCart) {
        console.debug('[GWP] Adding gift to cart')
        await this.addGiftToCart()
      } else if (!qualifiesForGift && this.gwpItemInCart) {
        console.debug('[GWP] Removing gift from cart')
        await this.removeGiftFromCart()
      }
    } catch (error) {
      console.error('[GWP] Error checking cart:', error)
    } finally {
      this.isProcessing = false
    }
  }

  async addGiftToCart() {
    try {
      const payload = {
        id: this.settings.variantId,
        quantity: 1,
        properties: {
          [this.settings.lineItemProperty]: 'true',
          _gwp_campaign: this.settings.campaignName,
        },
        sections: 'cart-drawer,header',
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.description || 'Failed to add gift to cart')
      }

      const data = await response.json()
      console.debug('[GWP] Gift added successfully:', data)

      // Update cart sections using Shopify's section rendering
      if (data.sections) {
        reRenderShopifySections(data)
      }

      // Dispatch cart change event to update UI
      document.dispatchEvent(new CustomEvent('cart:refresh'))
    } catch (error) {
      console.error('[GWP] Failed to add gift:', error)
    }
  }

  async removeGiftFromCart() {
    if (!this.gwpItemInCart) return

    const cartDrawer = document.querySelector('cart-drawer')
    if (cartDrawer) {
      cartDrawer.setAttribute('loading', true)
    }

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: this.gwpItemInCart.key,
          quantity: 0,
          sections: 'cart-drawer,header',
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.description || 'Failed to remove gift from cart')
      }

      const data = await response.json()
      console.debug('[GWP] Gift removed successfully', data)

      // Update cart sections using Shopify's section rendering
      if (data.sections) {
        reRenderShopifySections(data)
      }

      // Dispatch cart change event to update UI
      document.dispatchEvent(new CustomEvent('cart:refresh'))
    } catch (error) {
      console.error('[GWP] Failed to remove gift:', error)
    } finally {
      if (cartDrawer) {
        cartDrawer.setAttribute('loading', false)
      }
    }
  }

  isUSCustomer() {
    const country = window.Shopify.country
    return country === 'US'
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new GiftWithPurchase()
})
